import { useQuery } from "convex/react";
import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router";
import { api } from "../convex/_generated/api";
import AuthLayout from "./components/AuthLayout";
import ChatLayout from "./components/chat/ChatLayout";
import ChatPage from "./components/chat/ChatPage";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import WorkspaceLayout from "./components/workspace/WorkspaceLayout";
import { chatUrl, isChatHost, mainAppUrl } from "./lib/chatHost";
import SetupGoogleCallbackPage from "./modules/setup/SetupGoogleCallbackPage";
import SetupOutlookCallbackPage from "./modules/setup/SetupOutlookCallbackPage";
import SetupPage from "./modules/setup/SetupPage";
import Changelog from "./pages/Changelog";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Support from "./pages/Support";
import ClioPage from "./pages/staff/clio/ClioPage";
import FirmDetailPage from "./pages/staff/FirmDetailPage";
import FirmsPage from "./pages/staff/FirmsPage";
import MailPage from "./pages/staff/mail/MailPage";
import MailSettingsPage from "./pages/staff/mail/MailSettingsPage";
import ReviewPage from "./pages/staff/review/ReviewPage";
import StaffLayout from "./pages/staff/StaffLayout";
import ScenariosPage from "./pages/staff/scenarios/ScenariosPage";
import SkillsPage from "./pages/staff/skills/SkillsPage";
import AnthropicOAuthCallbackPage from "./pages/staff/subscription-accounts/AnthropicOAuthCallbackPage";
import LlmSubscriptionAccountsPage from "./pages/staff/subscription-accounts/LlmSubscriptionAccountsPage";
import TermsOfService from "./pages/TermsOfService";
import ApiKeysPage from "./pages/workspace/ApiKeysPage";
import BillingPage from "./pages/workspace/BillingPage";
import ClioConnectionCallbackPage from "./pages/workspace/ClioConnectionCallbackPage";
import CodexPage from "./pages/workspace/CodexPage";
import ConnectionsPage from "./pages/workspace/ConnectionsPage";
import GoogleConnectionCallbackPage from "./pages/workspace/GoogleConnectionCallbackPage";
import MembersPage from "./pages/workspace/MembersPage";
import OutlookConnectionCallbackPage from "./pages/workspace/OutlookConnectionCallbackPage";
import SettingsPage from "./pages/workspace/SettingsPage";
import ThreadsPage from "./pages/workspace/ThreadsPage";

function WorkspaceRedirect() {
	const isStaff = useQuery(api.staff.isCurrentUserStaff);
	if (isStaff === undefined) return null;
	return (
		<Navigate
			to={isStaff ? "/workspace/codex" : "/workspace/connections"}
			replace
		/>
	);
}

function StaffGuarded({ children }: { children: ReactNode }) {
	const isStaff = useQuery(api.staff.isCurrentUserStaff);
	if (isStaff === undefined) return null;
	if (!isStaff) return <Navigate to="/workspace/connections" replace />;
	return <>{children}</>;
}

// On clerical.com (the main host): any /chat* URL hops to the chat subdomain.
function ChatHostHop() {
	if (typeof window !== "undefined") {
		const { pathname, search, hash } = window.location;
		window.location.replace(`${chatUrl(pathname)}${search}${hash}`);
	}
	return null;
}

// On chat.clerical.com: any non-chat path hops back to the main host.
function MainHostHop() {
	if (typeof window !== "undefined") {
		const { pathname, search, hash } = window.location;
		window.location.replace(`${mainAppUrl(pathname)}${search}${hash}`);
	}
	return null;
}

export default function AppRoutes() {
	if (isChatHost()) {
		return (
			<Routes>
				<Route element={<ProtectedRoute />}>
					<Route element={<ChatLayout />}>
						<Route path="/chat" element={<ChatPage />} />
						<Route path="/chat/:threadId" element={<ChatPage />} />
					</Route>
				</Route>
				{/* Root → /chat. Every other path bounces back to clerical.com,
				    including /login, /setup/*, /workspace/*, and marketing pages —
				    all sign-in and OAuth lives on the main host. */}
				<Route path="/" element={<Navigate to="/chat" replace />} />
				<Route path="*" element={<MainHostHop />} />
			</Routes>
		);
	}

	return (
		<Routes>
			<Route element={<Layout />}>
				<Route path="/" element={<LandingPage />} />
				<Route path="/privacy" element={<PrivacyPolicy />} />
				<Route path="/terms" element={<TermsOfService />} />
				<Route path="/support" element={<Support />} />
				<Route path="/changelog" element={<Changelog />} />
			</Route>
			{/* In production, /chat* URLs bounce to chat.clerical.com. In dev
			    there's no chat subdomain, so chat is mounted in-tree below. */}
			{!import.meta.env.DEV && <Route path="/chat" element={<ChatHostHop />} />}
			{!import.meta.env.DEV && (
				<Route path="/chat/:threadId" element={<ChatHostHop />} />
			)}
			<Route element={<ProtectedRoute />}>
				<Route path="/setup" element={<SetupPage />} />
				<Route
					path="/setup/callback/google"
					element={<SetupGoogleCallbackPage />}
				/>
				<Route
					path="/setup/callback/outlook"
					element={<SetupOutlookCallbackPage />}
				/>
				{import.meta.env.DEV && (
					<Route element={<ChatLayout />}>
						<Route path="/chat" element={<ChatPage />} />
						<Route path="/chat/:threadId" element={<ChatPage />} />
					</Route>
				)}
				<Route element={<WorkspaceLayout />}>
					<Route path="/workspace" element={<WorkspaceRedirect />} />
					<Route
						path="/workspace/threads"
						element={
							<StaffGuarded>
								<ThreadsPage />
							</StaffGuarded>
						}
					/>
					<Route
						path="/workspace/threads/:id"
						element={
							<StaffGuarded>
								<ThreadsPage />
							</StaffGuarded>
						}
					/>
					<Route path="/workspace/connections" element={<ConnectionsPage />} />
					<Route path="/workspace/members" element={<MembersPage />} />
					<Route path="/workspace/settings" element={<SettingsPage />} />
					<Route
						path="/workspace/email-signature"
						element={<Navigate to="/workspace/settings" replace />}
					/>
					<Route
						path="/workspace/api-keys"
						element={
							<StaffGuarded>
								<ApiKeysPage />
							</StaffGuarded>
						}
					/>
					<Route path="/workspace/billing" element={<BillingPage />} />
					<Route
						path="/workspace/codex"
						element={
							<StaffGuarded>
								<CodexPage />
							</StaffGuarded>
						}
					/>
					<Route
						path="/workspace/connections/google/callback"
						element={<GoogleConnectionCallbackPage />}
					/>
					<Route
						path="/workspace/connections/outlook/callback"
						element={<OutlookConnectionCallbackPage />}
					/>
					<Route
						path="/workspace/connections/clio/callback"
						element={<ClioConnectionCallbackPage />}
					/>
					<Route
						element={
							<StaffGuarded>
								<StaffLayout />
							</StaffGuarded>
						}
					>
						<Route
							path="/staff"
							element={<Navigate to="/staff/firms" replace />}
						/>
						<Route path="/staff/firms" element={<FirmsPage />} />
						<Route path="/staff/firms/:tenantId" element={<FirmDetailPage />} />
					</Route>
					<Route
						path="/staff/review"
						element={
							<StaffGuarded>
								<ReviewPage />
							</StaffGuarded>
						}
					/>
					<Route
						path="/staff/scenarios"
						element={
							<StaffGuarded>
								<ScenariosPage />
							</StaffGuarded>
						}
					/>
					<Route
						path="/staff/skills"
						element={
							<StaffGuarded>
								<SkillsPage />
							</StaffGuarded>
						}
					/>
					<Route
						path="/staff/subscription-accounts"
						element={
							<StaffGuarded>
								<LlmSubscriptionAccountsPage />
							</StaffGuarded>
						}
					/>
					<Route
						path="/callback"
						element={
							<StaffGuarded>
								<AnthropicOAuthCallbackPage />
							</StaffGuarded>
						}
					/>
					<Route
						path="/staff/mail"
						element={
							<StaffGuarded>
								<MailPage />
							</StaffGuarded>
						}
					/>
					<Route
						path="/staff/mail/settings"
						element={
							<StaffGuarded>
								<MailSettingsPage />
							</StaffGuarded>
						}
					/>
					<Route
						path="/staff/clio"
						element={
							<StaffGuarded>
								<ClioPage />
							</StaffGuarded>
						}
					/>
				</Route>
			</Route>
			{import.meta.env.DEV && (
				<Route element={<WorkspaceLayout />}>
					<Route
						path="/dev/workspace"
						element={<Navigate to="/dev/workspace/threads" replace />}
					/>
					<Route path="/dev/workspace/threads" element={<ThreadsPage />} />
					<Route path="/dev/workspace/threads/:id" element={<ThreadsPage />} />
					<Route
						path="/dev/workspace/connections"
						element={<ConnectionsPage />}
					/>
					<Route path="/dev/workspace/settings" element={<SettingsPage />} />
					<Route
						path="/dev/workspace/email-signature"
						element={<Navigate to="/dev/workspace/settings" replace />}
					/>
					<Route
						path="/dev/workspace/connections/google/callback"
						element={<GoogleConnectionCallbackPage />}
					/>
					<Route
						path="/dev/workspace/connections/outlook/callback"
						element={<OutlookConnectionCallbackPage />}
					/>
					<Route
						path="/dev/workspace/connections/clio/callback"
						element={<ClioConnectionCallbackPage />}
					/>
				</Route>
			)}
			{/* Public: invite accept (design 021) — must work with no session and
			    no active firm, so it lives outside ProtectedRoute. */}
			<Route path="/invite/:token" element={<InviteAcceptPage />} />
			<Route element={<AuthLayout />}>
				<Route path="/login" element={<LoginPage />} />
				<Route
					path="/select-firm"
					element={<Navigate to="/workspace" replace />}
				/>
				<Route path="/get-started" element={<Navigate to="/login" replace />} />
				<Route path="/firm-login" element={<Navigate to="/login" replace />} />
			</Route>
		</Routes>
	);
}
